import { useEffect, useState, type FormEvent } from "react";
import {
  Copy,
  Download,
  KeyRound,
  Plus,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import type { AgentIdentity, AgentIdentityInput } from "@mk/core";
import { api } from "../api";
import { Modal } from "./ui";

const empty: AgentIdentityInput = {
  name: "",
  machine: "http-client",
  harness: "http",
  instructions: "",
};
export function AgentIdentities({ onClose }: { onClose: () => void }) {
  const [items, setItems] = useState<AgentIdentity[]>([]);
  const [available, setAvailable] = useState(true);
  const [selected, setSelected] = useState<AgentIdentity | null>(null);
  const [draft, setDraft] = useState({ ...empty });
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [exported, setExported] = useState("");
  async function load() {
    const data = await api<{ available: boolean; identities: AgentIdentity[] }>(
      "/v1/admin/identities",
    );
    setItems(data.identities);
    setAvailable(data.available);
  }
  useEffect(() => {
    void load().catch((e) => setError(e.message));
  }, []);
  function select(item: AgentIdentity | null) {
    setSelected(item);
    setDraft(
      item
        ? {
            name: item.name,
            machine: item.machine,
            harness: item.harness,
            instructions: item.instructions,
          }
        : { ...empty },
    );
    setEditing(true);
    setExported("");
    setError("");
    setNotice("");
  }
  async function action(fn: () => Promise<void>) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function save(e: FormEvent) {
    e.preventDefault();
    await action(async () => {
      const item = await api<AgentIdentity>(
        selected
          ? `/v1/admin/identities/${selected.id}`
          : "/v1/admin/identities",
        selected ? "PATCH" : "POST",
        draft,
      );
      setSelected(item);
      setExported("");
      setNotice(
        "Identity saved. Copy its connection instructions to your agent.",
      );
    });
  }
  async function exportFile() {
    if (!selected) return;
    await action(async () => {
      const result = await api<{ markdown: string }>(
        `/v1/admin/identities/${selected.id}/export`,
      );
      setExported(result.markdown);
    });
  }
  async function changeAccess(kind: "rotate" | "toggle" | "delete") {
    if (!selected) return;
    const enable = kind === "toggle" && !selected.enabled;
    if (
      !enable &&
      !window.confirm(
        `${kind === "delete" ? "Delete" : kind === "rotate" ? "Rotate the token for" : "Revoke access for"} ${selected.name}? Existing sessions will stop and active task claims will be released. Saved work will remain.`,
      )
    )
      return;
    await action(async () => {
      setExported("");
      if (kind === "delete") {
        await api(`/v1/admin/identities/${selected.id}`, "DELETE");
        setSelected(null);
        setEditing(false);
        setNotice("Identity deleted. Its work history is preserved.");
      } else {
        const updated = await api<AgentIdentity>(
          `/v1/admin/identities/${selected.id}${kind === "rotate" ? "/rotate" : ""}`,
          kind === "rotate" ? "POST" : "PATCH",
          kind === "toggle" ? { enabled: enable } : undefined,
        );
        setSelected(updated);
        setNotice(
          kind === "rotate"
            ? "Token rotated. Copy the updated identity to your agent; previous sessions are invalid."
            : enable
              ? "Identity enabled. Its token can create new sessions."
              : "Access revoked. Its token and existing sessions can no longer access the board.",
        );
      }
    });
  }
  return (
    <Modal title="Agent identities" wide onClose={onClose}>
      <div className="identity-panel">
        <div className="identity-intro">
          <ShieldCheck size={20} />
          <p>
            Create an identity, give its connection instructions to an agent,
            and revoke access when you are done. All identities have agent
            access to this board; your approval remains required for human-only
            actions.
          </p>
        </div>
        {error && (
          <div className="form-error" role="alert">
            {error}
          </div>
        )}
        {notice && (
          <p className="identity-notice" role="status">
            {notice}
          </p>
        )}
        {!available ? (
          <p>Agent identities are not enabled on this installation.</p>
        ) : (
          <div className="identity-layout">
            <section
              className="identity-list"
              aria-label="Saved agent identities"
            >
              <div className="identity-list-actions">
                <button
                  className="button primary"
                  onClick={() => select(null)}
                  disabled={busy}
                >
                  <Plus size={16} />
                  New identity
                </button>
                <button
                  className="button"
                  aria-label="Refresh identities"
                  onClick={() => void action(load)}
                  disabled={busy}
                >
                  <RefreshCw size={16} />
                </button>
              </div>
              {!items.length && (
                <p>No identities yet. Create one for your first agent.</p>
              )}
              {items.map((item) => (
                <button
                  type="button"
                  className={`identity-item ${selected?.id === item.id ? "selected" : ""}`}
                  key={item.id}
                  onClick={() => select(item)}
                  disabled={busy}
                >
                  <strong>{item.name}</strong>
                  <span>
                    {item.enabled ? "Active" : "Revoked"} · token …
                    {item.token_suffix}
                  </span>
                  <small>
                    Last used:{" "}
                    {item.last_used_at
                      ? new Date(item.last_used_at).toLocaleString()
                      : "Never"}
                  </small>
                </button>
              ))}
            </section>
            <section className="identity-editor" aria-label="Identity editor">
              {!editing ? (
                <p>Select an identity or create one to get started.</p>
              ) : (
                <>
                  <form onSubmit={save} className="identity-form">
                    <h3>{selected ? selected.name : "New identity"}</h3>
                    <label>
                      Identity name
                      <input
                        required
                        maxLength={100}
                        pattern="[a-zA-Z0-9][a-zA-Z0-9._\-]*"
                        placeholder="minimax.io"
                        value={draft.name}
                        onChange={(e) =>
                          setDraft({ ...draft, name: e.target.value })
                        }
                      />
                    </label>
                    <div className="identity-fields">
                      <label>
                        Machine
                        <input
                          required
                          maxLength={100}
                          value={draft.machine}
                          onChange={(e) =>
                            setDraft({ ...draft, machine: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        Harness
                        <input
                          required
                          maxLength={100}
                          value={draft.harness}
                          onChange={(e) =>
                            setDraft({ ...draft, harness: e.target.value })
                          }
                        />
                      </label>
                    </div>
                    <label htmlFor="identity-instructions">
                      IDENTITY.md instructions
                    </label>
                    <textarea
                      id="identity-instructions"
                      rows={6}
                      maxLength={20000}
                      placeholder="What should this agent know or do?"
                      value={draft.instructions}
                      onChange={(e) =>
                        setDraft({ ...draft, instructions: e.target.value })
                      }
                    />
                    <small>
                      The URL, identity and token are added automatically when
                      you export. Changing the name, machine or harness ends
                      existing sessions.
                    </small>
                    <button className="button primary" disabled={busy}>
                      Save identity
                    </button>
                  </form>
                  {selected && (
                    <div className="identity-controls">
                      <button
                        className="button"
                        disabled={busy}
                        onClick={() => void exportFile()}
                      >
                        <KeyRound size={16} />
                        Show connection instructions
                      </button>
                      <button
                        className="button"
                        disabled={busy}
                        onClick={() => void changeAccess("rotate")}
                      >
                        Rotate token
                      </button>
                      <button
                        className="button"
                        disabled={busy}
                        onClick={() => void changeAccess("toggle")}
                      >
                        {selected.enabled ? "Revoke access" : "Enable access"}
                      </button>
                      <button
                        className="button danger"
                        disabled={busy}
                        onClick={() => void changeAccess("delete")}
                      >
                        Delete identity
                      </button>
                    </div>
                  )}
                  {exported && (
                    <div className="identity-export">
                      <label htmlFor="identity-connection">
                        Connection instructions
                      </label>
                      <textarea
                        id="identity-connection"
                        readOnly
                        rows={12}
                        value={exported}
                      />
                      <p>
                        Give this to your agent privately. It includes a working
                        credential. The agent must be able to send HTTP requests
                        with custom headers.
                      </p>
                      <div className="identity-controls">
                        <button
                          className="button"
                          onClick={() =>
                            void action(async () => {
                              await navigator.clipboard.writeText(exported);
                              setNotice("Identity copied.");
                            })
                          }
                        >
                          <Copy size={16} />
                          Copy IDENTITY.md
                        </button>
                        <button
                          className="button"
                          onClick={() => {
                            const url = URL.createObjectURL(
                              new Blob([exported], { type: "text/markdown" }),
                            );
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = "IDENTITY.md";
                            a.click();
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                          }}
                        >
                          <Download size={16} />
                          Download IDENTITY.md
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </Modal>
  );
}
